import { z } from "zod";
// =============================================================================
// LINE DRIVE
// =============================================================================
/**
 * What determines an addition line's rate. Matches ferm_line_drive_enum.
 *
 * This is a BrewBase concept rather than a projection of BrewControl wiring:
 * authoring has to work for fermentations running on no automation at all,
 * where a pulse plan is an instruction a person executes.
 */
export const LineDrive = {
    /** An authored rate the line is driven to. */
    RATE: "RATE",
    /** Repeated fixed doses at an interval. */
    PULSE: "PULSE",
    /** Rate set by a pH controller; not authored on this line. */
    PH_STAT: "PH_STAT",
    /** Likewise, for dissolved oxygen. */
    DO_STAT: "DO_STAT",
};
export const lineDriveSchema = z.enum([
    LineDrive.RATE,
    LineDrive.PULSE,
    LineDrive.PH_STAT,
    LineDrive.DO_STAT,
]);
export const LINE_DRIVE_LABELS = {
    [LineDrive.RATE]: "Rate",
    [LineDrive.PULSE]: "Pulsed",
    [LineDrive.PH_STAT]: "pH-stat",
    [LineDrive.DO_STAT]: "DO-stat",
};
export const LINE_DRIVE_DESCRIPTIONS = {
    [LineDrive.RATE]: "Driven to an authored rate",
    [LineDrive.PULSE]: "Fixed doses repeated at an interval",
    [LineDrive.PH_STAT]: "Rate determined by the pH controller",
    [LineDrive.DO_STAT]: "Rate determined by the DO controller",
};
/** Drives whose rate is set by a controller, so nothing is authored on the line. */
export function isStatDrive(drive) {
    return drive === LineDrive.PH_STAT || drive === LineDrive.DO_STAT;
}
// =============================================================================
// LINE-SCOPED PARAMETERS
// =============================================================================
/**
 * The parameter types that only exist in the context of an addition line.
 *
 * A line parameter is an ordinary `fermentation_parameter` /
 * `fermentation_protocol_parameter` row that carries a `line_type_id` and one
 * of these four generic types. Before the line split each line needed its own
 * parameter type (`methanol_feed`, `base_feed`, ...), which was line identity
 * encoded in a value registry; now the type says what the value *is* and the
 * line says what it belongs to.
 *
 * This lives in code rather than in a `scope` column for the same reason
 * capabilities do: it is a four-row fact that the services, the dispatch
 * namespace and the authoring UI all have to agree on, and a column would be a
 * second source of truth for it.
 */
export const LINE_PARAMETER_CODES = [
    "rate_sp",
    "specific_rate_sp",
    "pulse_interval",
    "pulse_amount",
    "pulse_duration",
    "pulse_pump_speed",
];
export function isLineParameterCode(code) {
    return LINE_PARAMETER_CODES.includes(code);
}
/**
 * The line parameters an operator authors for a given drive.
 *
 * Ordered; the first entry is the line's headline value -- the one a feeds
 * panel shows when it has room for one number. A stat drive authors nothing,
 * because its rate comes from a controller.
 */
export function lineParameterCodesForDrive(drive) {
    switch (drive) {
        case LineDrive.RATE:
            return ["rate_sp", "specific_rate_sp"];
        case LineDrive.PULSE:
            return ["pulse_amount", "pulse_interval", "pulse_duration", "pulse_pump_speed"];
        case LineDrive.PH_STAT:
        case LineDrive.DO_STAT:
            return [];
    }
}
/**
 * The setpoint a stat-driven line is controlled from.
 *
 * Derivable from the drive, which is why the procedure line does not store it:
 * a PH_STAT line is by definition driven from `ph_sp`, and the column that used
 * to hold this was `ph_sp` on every row that had one and read by nothing.
 */
export function statSetpointCodeForDrive(drive) {
    switch (drive) {
        case LineDrive.PH_STAT:
            return "ph_sp";
        case LineDrive.DO_STAT:
            return "do_sp";
        case LineDrive.RATE:
        case LineDrive.PULSE:
            return null;
    }
}
/**
 * Asserts that a parameter's type and its line agree about scope.
 *
 * A line-scoped type without a line is a row nothing can resolve -- every
 * lookup is keyed by (type, line) and a null line silently means "the
 * fermentation-level one". A non-line-scoped type *with* a line claims a
 * `temperature` belongs to the methanol plumbing, which no reader honours.
 *
 * Throws a plain `Error`; API services wrap it in a `TRPCError`.
 */
export function assertLineScope(typeCode, lineTypeId) {
    const scoped = isLineParameterCode(typeCode);
    if (scoped && lineTypeId == null) {
        throw new Error(`Parameter type "${typeCode}" is line-scoped and requires an addition line`);
    }
    if (!scoped && lineTypeId != null) {
        throw new Error(`Parameter type "${typeCode}" is not line-scoped and cannot be attached to an addition line`);
    }
}
// =============================================================================
// VALUE SHAPE
// =============================================================================
/**
 * A numeric parameter's authored value: a constant, or a ramp to it.
 *
 * There is deliberately no discriminator field: a ramp is a constant with a
 * duration, so a row cannot claim to be one shape while holding another. Where
 * the ramp starts from is not authored -- the resolver reads it from the value
 * in effect when the change is applied (docs/decisions/0001-parameter-model.md).
 *
 * Applies to every numeric parameter, not just feeds. Temperature can ramp.
 */
export const parameterValueSchema = z.object({
    /** Constant value, or a ramp's target. */
    numberValue: z.number().nullable(),
    /** Hours the ramp takes. Null means the value is set at once. */
    rampDurationH: z.number().positive().nullable(),
});
export const EMPTY_PARAMETER_VALUE = {
    numberValue: null,
    rampDurationH: null,
};
// =============================================================================
// DISPLAY
// =============================================================================
function trim(n) {
    return Number.isInteger(n) ? String(n) : String(Number(n.toFixed(4)));
}
/**
 * How a parameter is named to an operator.
 *
 * Line parameters are generic types shared by every addition line, so the type
 * name alone names nothing: "Rate setpoint" and "Pulse amount" say which knob
 * but not which plumbing. The line goes in front, the way
 * `parameterVariableName` puts the line code in front of the type code.
 *
 * The type name is lowercased because the line name carries the capital
 * ("Methanol Feed rate setpoint"). A fermentation-level parameter is its type
 * name unchanged.
 */
export function parameterLabel(typeName, lineName) {
    if (!lineName)
        return typeName;
    return `${lineName} ${typeName.toLowerCase()}`;
}
export function formatValueSummary(value, unit) {
    if (value.numberValue === null) {
        return value.rampDurationH !== null ? "Ramp (no target)" : "Not set";
    }
    const u = unit ? ` ${unit}` : "";
    const over = value.rampDurationH !== null ? ` over ${trim(value.rampDurationH)}h` : "";
    return `${value.rampDurationH !== null ? "→" : ""}${trim(value.numberValue)}${u}${over}`;
}
// =============================================================================
// FORMULA VARIABLE NAMING
// =============================================================================
/**
 * The name a parameter takes in formulas and KPI variables.
 *
 * Line parameters are generic parameter types (`rate_sp`, `pulse_*`) shared by
 * every addition line, so a type code no longer identifies a value.
 * Composition is uniform — `${lineCode}_${typeCode}` — so a line's rate
 * setpoint is `methanol_feed_rate_sp` and its telemetry counterpart carries
 * the same name.
 *
 * The rate parameter used to be the one exception: type code `feed_rate` took
 * the bare line code (`methanol_feed`), because that was what stored formulas
 * said before the line split. The exception is gone; the migration
 * 20260902160659_feed_rate_knob_to_rate_sp rewrote those formulas.
 *
 * This has to be the only implementation. It was open-coded at four sites and
 * two of them got it wrong, which silently collapsed every line's computed feed
 * onto a single variable.
 */
export function parameterVariableName(typeCode, lineCode) {
    if (!lineCode)
        return typeCode;
    return `${lineCode}_${typeCode}`;
}
/**
 * The inverse of `parameterVariableName`: which line, if any, a variable name
 * belongs to. The longest live line code that prefixes the name wins, so
 * `methanol_feed_rate_sp` is `rate_sp` on `methanol_feed` even when a line
 * `methanol` also exists. A name no line prefixes is a fermentation-level
 * type code.
 */
export function parseVariableName(name, lineCodes) {
    let best = null;
    for (const line of lineCodes) {
        if (!line || !name.startsWith(`${line}_`) || name.length <= line.length + 1)
            continue;
        if (best === null || line.length > best.length)
            best = line;
    }
    if (best === null)
        return { lineCode: null, typeCode: name };
    return { lineCode: best, typeCode: name.slice(best.length + 1) };
}
//# sourceMappingURL=parameter-values.js.map